import React, { useState } from 'react';
import { Beer, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useLocationScope } from '../context/LocationContext';
import { canCheckInAtBar } from '../data/checkInPhrases';
import { CHECK_IN_MAX_FEET, getDistanceFeet } from '../utils/geo';

function CheckInButton({ bar, compact = false }) {
  const { user } = useAuth();
  const { getUserRating, addCheckIn } = useApp();
  const { userLocation, requestLocation, locationLoading } = useLocationScope();
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!user || !bar) return null;

  const userRating = getUserRating(bar.id);
  const unlocked = canCheckInAtBar(userRating);

  const handleCheckIn = async () => {
    if (!unlocked || isSubmitting) return;
    setStatus('');

    let coords = userLocation;
    if (!coords) {
      requestLocation();
      setStatus('Turn on location to check in at this bar.');
      return;
    }

    const feet = getDistanceFeet(coords.lat, coords.lng, bar.lat, bar.lng);
    if (feet > CHECK_IN_MAX_FEET) {
      setStatus(`You need to be within ${CHECK_IN_MAX_FEET} feet of ${bar.name}.`);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await addCheckIn(bar.id);
      setStatus(result?.message_text || 'Checked in.');
    } catch (err) {
      setStatus(err.message || 'Could not check in right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const busy = isSubmitting || locationLoading;

  if (compact) {
    return (
      <div className="mb-3">
        <button
          type="button"
          onClick={handleCheckIn}
          disabled={!unlocked || busy}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
            unlocked
              ? 'bg-dive-amber text-black hover:bg-amber-400'
              : 'bg-dive-gray text-gray-500 cursor-not-allowed'
          }`}
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Beer className="w-5 h-5" />}
          {isSubmitting ? 'Checking in...' : "I'm here"}
        </button>
        <p className="text-gray-500 text-xs mt-2 text-center">
          {unlocked
            ? `Location on, within ${CHECK_IN_MAX_FEET} feet.`
            : 'Rate this bar once to unlock check-ins.'}
        </p>
        {status && <p className="text-dive-amber text-sm mt-2 text-center">{status}</p>}
      </div>
    );
  }

  return (
    <div className="bg-dive-darker border border-dive-gray rounded-xl p-4">
      <button
        type="button"
        onClick={handleCheckIn}
        disabled={!unlocked || busy}
        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
          unlocked
            ? 'bg-dive-amber text-black hover:bg-amber-400'
            : 'bg-dive-gray text-gray-500 cursor-not-allowed'
        }`}
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Beer className="w-5 h-5" />}
        {isSubmitting ? 'Checking in...' : "I'm here"}
      </button>
      <p className="text-gray-500 text-xs mt-2 text-center">
        {unlocked
          ? `Tell your friends you bellied up — you must be within ${CHECK_IN_MAX_FEET} feet.`
          : 'Rate this bar once to unlock check-ins.'}
      </p>
      {status && (
        <p className="text-dive-amber text-sm mt-3 text-center">{status}</p>
      )}
    </div>
  );
}

export default CheckInButton;
